import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  Image,
  VStack,
  HStack,
  SimpleGrid,
  useColorModeValue,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import ModernNavbar from '../component/header/ModernNavbar';

// Create a proper motion wrapper that doesn't pass motion props to DOM
const MotionWrapper = ({ children, ...motionProps }) => {
  return (
    <motion.div {...motionProps}>
      {children}
    </motion.div>
  );
};

const ModernExhibitions = () => {
  const bgColor = useColorModeValue('white', 'neutral.900');
  const textColor = useColorModeValue('neutral.900', 'white');
  const cardBg = useColorModeValue('white', 'neutral.800');

  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0 },
  };

  const floor1Images = [
    '/assets/images/singha/floor1/IMG_7820.webp',
    '/assets/images/singha/floor1/IMG_7825.webp',
    '/assets/images/singha/floor1/IMG_7828.webp',
    '/assets/images/singha/floor1/IMG_7829.webp',
    '/assets/images/singha/floor1/IMG_7830.webp',
    '/assets/images/singha/floor1/IMG_7833.webp',
  ];

  const floor2Images = [
    '/assets/images/singha/floor2/IMG_7899.webp',
    '/assets/images/singha/floor2/IMG_7900.webp',
    '/assets/images/singha/floor2/IMG_7903.webp',
    '/assets/images/singha/floor2/IMG_7904.webp',
    '/assets/images/singha/floor2/IMG_7905.webp',
    '/assets/images/singha/floor2/IMG_7906.webp',
  ];

  return (
    <Box>
      <ModernNavbar />
      
      {/* Hero Section */}
      <Box
        bgImage="url('/assets/images/singha/floor1/IMG_7829.webp')"
        bgSize="cover"
        bgPosition="center"
        bgRepeat="no-repeat"
        position="relative"
        py={20}
      >
        <Box
          position="absolute"
          top={0}
          left={0}
          w="100%"
          h="100%"
          bg="rgba(36, 36, 36, 0.5)"
        />
        <Container maxW="1200px" position="relative" zIndex={2}>
          <MotionWrapper
           
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.8 }}
          >
            <VStack spacing={6} textAlign="center" color="white">
              <Heading
                as="h1"
                size="2xl"
                fontFamily="heading"
                fontWeight="normal"
              >
                นิทรรศการ
              </Heading>
              <Text fontSize="xl" maxW="800px" lineHeight="1.6">
                สำรวจนิทรรศการที่น่าสนใจของพิพิธภัณฑ์สิงห์ท่า 
                ที่นำเสนอประวัติศาสตร์และวัฒนธรรมของชุมชนบ้านสิงห์ท่า
              </Text>
            </VStack>
          </MotionWrapper>
        </Container>
      </Box>

      {/* Floor 1 Exhibition */}
      <Box py={20} bg={bgColor} id="floor-1">
        <Container maxW="1200px">
          <MotionWrapper
           
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <VStack spacing={12}>
              <VStack spacing={4} textAlign="center">
                <Heading
                  as="h2"
                  size="xl"
                  fontFamily="heading"
                  color={textColor}
                >
                  นิทรรศการชั้น 1
                </Heading>
                <Text fontSize="lg" color={textColor} maxW="800px">
                  นิทรรศการที่บอกเล่าเรื่องราวความเป็นมาของชุมชนบ้านสิงห์ท่า 
                  ตั้งแต่ยุคก่อบ้านตั้งเมืองจนถึงปัจจุบัน
                </Text>
              </VStack>

              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
                {floor1Images.map((image, index) => (
                  <MotionWrapper
                    key={index}
                   
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: index * 0.1 }}
                  >
                    <Box
                      bg={cardBg}
                      borderRadius="lg"
                      overflow="hidden"
                      boxShadow="lg"
                      transition="transform 0.3s ease"
                      _hover={{ transform: 'translateY(-8px)' }}
                    >
                      <Image
                        src={image}
                        alt={`Floor 1 Exhibition ${index + 1}`}
                        w="100%"
                        h="250px"
                        objectFit="cover"
                      />
                      <Box p={6}>
                        <Heading as="h3" size="md" mb={2} fontFamily="heading">
                          ส่วนจัดแสดง {index + 1}
                        </Heading>
                        <Text color={textColor} fontSize="sm">
                          สำรวจประวัติศาสตร์และวัฒนธรรมของชุมชนบ้านสิงห์ท่า
                        </Text>
                      </Box>
                    </Box>
                  </MotionWrapper>
                ))}
              </SimpleGrid>
            </VStack>
          </MotionWrapper>
        </Container>
      </Box>

      {/* Floor 2 Exhibition */}
      <Box py={20} bg="neutral.50" id="floor-2">
        <Container maxW="1200px">
          <MotionWrapper
           
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <VStack spacing={12}>
              <VStack spacing={4} textAlign="center">
                <Heading
                  as="h2"
                  size="xl"
                  fontFamily="heading"
                  color="neutral.900"
                >
                  นิทรรศการชั้น 2
                </Heading>
                <Text fontSize="lg" color="neutral.700" maxW="800px">
                  นิทรรศการที่แสดงวิถีชีวิตและวัฒนธรรมของชุมชนบ้านสิงห์ท่า 
                  ในรูปแบบที่ทันสมัยและน่าสนใจ
                </Text>
              </VStack>

              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
                {floor2Images.map((image, index) => (
                  <MotionWrapper
                    key={index}
                   
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: index * 0.1 }}
                  >
                    <Box
                      bg={cardBg}
                      borderRadius="lg"
                      overflow="hidden"
                      boxShadow="lg"
                      transition="transform 0.3s ease"
                      _hover={{ transform: 'translateY(-8px)' }}
                    >
                      <Image
                        src={image}
                        alt={`Floor 2 Exhibition ${index + 1}`}
                        w="100%"
                        h="250px"
                        objectFit="cover"
                      />
                      <Box p={6}>
                        <Heading as="h3" size="md" mb={2} fontFamily="heading">
                          ส่วนจัดแสดง {index + 1}
                        </Heading>
                        <Text color={textColor} fontSize="sm">
                          สำรวจวิถีชีวิตและวัฒนธรรมของชุมชนบ้านสิงห์ท่า
                        </Text>
                      </Box>
                    </Box>
                  </MotionWrapper>
                ))}
              </SimpleGrid>
            </VStack>
          </MotionWrapper>
        </Container>
      </Box>

      {/* Special Exhibitions */}
      <Box py={20} bg={bgColor}>
        <Container maxW="1200px">
          <MotionWrapper
           
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <VStack spacing={12}>
              <VStack spacing={4} textAlign="center">
                <Heading
                  as="h2"
                  size="xl"
                  fontFamily="heading"
                  color={textColor}
                >
                  นิทรรศการพิเศษ
                </Heading>
                <Text fontSize="lg" color={textColor} maxW="800px">
                  นิทรรศการหมุนเวียนที่นำเสนอเรื่องราวพิเศษและกิจกรรมที่น่าสนใจ
                </Text>
              </VStack>

              <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={12}>
                <Box
                  bg={cardBg}
                  p={8}
                  borderRadius="lg"
                  boxShadow="lg"
                >
                  <Heading as="h3" size="lg" mb={4} fontFamily="heading" color="brand.500">
                    นิทรรศการหมุนเวียน
                  </Heading>
                  <Text color={textColor} lineHeight="1.6" mb={4}>
                    นิทรรศการที่เปลี่ยนไปตามช่วงเวลาและโอกาสพิเศษ 
                    เพื่อนำเสนอเรื่องราวและมุมมองใหม่ๆ เกี่ยวกับชุมชนบ้านสิงห์ท่า
                  </Text>
                  <Button
                    bg="brand.500"
                    _hover={{ bg: 'brand.600' }}
                    onClick={() => window.location.href = '/gallery'}
                  >
                    ดูนิทรรศการปัจจุบัน
                  </Button>
                </Box>
                
                <Box
                  bg={cardBg}
                  p={8}
                  borderRadius="lg"
                  boxShadow="lg"
                >
                  <Heading as="h3" size="lg" mb={4} fontFamily="heading" color="brand.500">
                    กิจกรรมพิเศษ
                  </Heading>
                  <Text color={textColor} lineHeight="1.6" mb={4}>
                    กิจกรรมต่างๆ ที่จัดขึ้นในพิพิธภัณฑ์ เช่น การบรรยาย 
                    การสาธิตงานฝีมือ การแสดงวัฒนธรรม และกิจกรรมสำหรับเด็ก
                  </Text>
                  <Button
                    bg="brand.500"
                    _hover={{ bg: 'brand.600' }}
                    onClick={() => window.location.href = '/contact'}
                  >
                    ติดต่อสอบถาม
                  </Button>
                </Box>
              </SimpleGrid>
            </VStack>
          </MotionWrapper>
        </Container>
      </Box>

      {/* Call to Action */}
      <Box py={20} bg="brand.500">
        <Container maxW="1200px">
          <MotionWrapper
           
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <VStack spacing={8} textAlign="center" color="white">
              <Heading
                as="h2"
                size="xl"
                fontFamily="heading"
              >
                เยี่ยมชมนิทรรศการ
              </Heading>
              <Text fontSize="lg" maxW="600px" opacity={0.9}>
                พบกับประสบการณ์การเรียนรู้ที่ไม่มีใครเหมือน 
                ผ่านนิทรรศการที่บอกเล่าเรื่องราวของชุมชนและวัฒนธรรมท้องถิ่น
              </Text>
              <HStack spacing={4}>
                <Button
                  size="lg"
                  bg="white"
                  color="brand.500"
                  _hover={{ bg: 'neutral.50' }}
                  onClick={() => window.location.href = '/contact'}
                >
                  จองเข้าชม
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  borderColor="white"
                  color="white"
                  _hover={{ bg: 'white', color: 'brand.500' }}
                  onClick={() => window.location.href = '/gallery'}
                >
                  ดูภาพถ่าย
                </Button>
              </HStack>
            </VStack>
          </MotionWrapper>
        </Container>
      </Box>
    </Box>
  );
};

export default ModernExhibitions; 